import { Paperclip, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Dialog, DialogContent, DialogSrTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/menu';
import { MentionPicker } from '@/components/tasks/MentionAutocomplete';
import { useMentionInput } from '@/hooks/useMentionField';
import { PriorityDot } from '@/components/tasks/PriorityDot';
import { useT, type MessageKey } from '@/lib/i18n';
import { PRIORITY_LABELS, PRIORITY_ORDER } from '@/lib/priority';
import { isAcceptedFile, usePasteFiles } from '@/hooks/useAttachments';
import { attachments as attachmentsApi } from '@/lib/api/endpoints';
import { useToast } from '@/components/ui/toast';
import { useCreateTask } from '@/hooks/useTasks';
import type { TaskCreateInput, TaskPriority } from '@/lib/api/types';
import { cn } from '@/lib/utils';

type Destination = 'today' | 'tomorrow' | 'backlog';

const DESTINATION_LABELS: Record<Destination, MessageKey> = {
  today: 'move.today',
  tomorrow: 'move.tomorrow',
  backlog: 'move.backlog',
};

/**
 * Quick Add.
 *
 * The whole point is speed: type a title, press Enter, done. Everything else —
 * destination, priority, delegation — is reachable without leaving the
 * keyboard, and nothing is required.
 *
 * Typing `@` opens the user picker. Choosing someone sets an explicit
 * `assigned_to_user_id`; the `@handle` stays in the title only as text, because
 * the backend deliberately does not parse assignment out of prose.
 */
export function QuickAdd({
  open,
  onOpenChange,
  defaultDestination = 'today',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDestination?: 'today' | 'backlog';
}) {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent position="top">
        <DialogSrTitle>{t('command.newTask')}</DialogSrTitle>
        {/* Radix unmounts a closed dialog, so the form starts empty on every
            open without a reset effect. Reopening never resumes a stale draft. */}
        <QuickAddForm
          defaultDestination={defaultDestination}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function QuickAddForm({
  defaultDestination,
  onDone,
}: {
  defaultDestination: 'today' | 'backlog';
  onDone: () => void;
}) {
  const t = useT();
  const { field, inputRef } = useMentionInput();
  const [destination, setDestination] = useState<Destination>(defaultDestination);
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [pending, setPending] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const createTask = useCreateTask();
  const toast = useToast();

  /**
   * `⌘V` of a screenshot.
   *
   * The task does not exist yet, so the file is held here and uploaded once the
   * task has an id. That is what makes the headline flow work: `⌘⇧4`, `⌘V`,
   * type the title, Enter — one pass, no visit to the task afterwards.
   */
  const attach = useCallback(
    (files: File[]) => {
      const accepted = files.filter((file) => {
        if (isAcceptedFile(file)) return true;
        toast.error(t('attachment.unsupportedType', { name: file.name }));
        return false;
      });
      if (accepted.length) setPending((current) => [...current, ...accepted]);
    },
    [toast, t],
  );

  usePasteFiles(attach);

  const title = field.value.trim();
  const canSubmit = title.length > 0 && !createTask.isPending && !uploading;

  function submit() {
    if (!canSubmit) return;

    const input: TaskCreateInput = {
      title,
      priority,
      schedule: destination,
      ...(field.assignee ? { assigned_to_user_id: field.assignee.id } : {}),
    };

    createTask.mutate(input, {
      onSuccess: async (task) => {
        if (pending.length === 0) {
          onDone();
          return;
        }
        // The task exists now, so the held files finally have somewhere to go.
        setUploading(true);
        try {
          for (const file of pending) {
            await attachmentsApi.upload(task.id, file);
          }
        } catch {
          // The task itself was created; say so rather than implying it failed.
          toast.error(t('quickAdd.attachmentFailed'));
        } finally {
          setUploading(false);
          onDone();
        }
      },
    });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="relative p-3">
        <input
          ref={inputRef}
          autoFocus
          value={field.value}
          onChange={(event) =>
            field.handleChange(event.target.value, event.target.selectionStart ?? 0)
          }
          onKeyDown={(event) => {
            // Enter saves — explicitly, rather than leaning on the browser's
            // implicit form submission, which depends on the form's exact shape
            // and does not fire in every environment. The spec promises this
            // key works, so it should not be a side effect of markup.
            //
            // While the mention picker is open it owns Enter (it selects a
            // user), and its capture-phase listener runs first.
            if (event.key === 'Enter' && !field.mention) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={t('quickAdd.prompt')}
          aria-label={t('task.title')}
          className={cn(
            'w-full bg-transparent text-[15px] text-content outline-none',
            'placeholder:text-content-subtle',
          )}
        />

        {field.mention ? (
          <MentionPicker
            query={field.mention.query}
            onSelect={field.selectUser}
            onDismiss={field.dismissMention}
            inputRef={inputRef}
          />
        ) : null}
      </div>

      {pending.length > 0 ? (
        <ul className="flex flex-wrap gap-2 px-3 pb-2">
          {pending.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="group relative overflow-hidden rounded border border-border"
            >
              {file.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  // Revoked once painted; holding the blob URL open for the
                  // life of the dialog would leak it.
                  onLoad={(event) => URL.revokeObjectURL(event.currentTarget.src)}
                  className="h-16 w-24 bg-surface-sunken object-cover"
                />
              ) : (
                <span className="flex h-16 w-24 items-center justify-center bg-surface-sunken px-2 text-center text-[10px] text-content-subtle">
                  {file.name}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  setPending((current) => current.filter((_, i) => i !== index))
                }
                aria-label={t('attachment.remove', { name: file.name })}
                className="absolute right-0.5 top-0.5 rounded bg-surface-raised/90 p-0.5 text-content-muted opacity-0 transition-surface hover:text-danger group-hover:opacity-100"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex items-center gap-1.5 border-t border-border px-3 py-2">
        <Menu>
          <MenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm">
              {t(DESTINATION_LABELS[destination])}
            </Button>
          </MenuTrigger>
          <MenuContent>
            {(Object.keys(DESTINATION_LABELS) as Destination[]).map((value) => (
              <MenuItem key={value} onSelect={() => setDestination(value)}>
                {t(DESTINATION_LABELS[value])}
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>

        <Menu>
          <MenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm">
              <PriorityDot priority={priority} />
              {t(PRIORITY_LABELS[priority])}
            </Button>
          </MenuTrigger>
          <MenuContent>
            {PRIORITY_ORDER.map((value) => (
              <MenuItem key={value} onSelect={() => setPriority(value)}>
                <PriorityDot priority={value} />
                {t(PRIORITY_LABELS[value])}
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>

        {field.assignee ? (
          <span className="ml-1 rounded bg-accent-subtle px-1.5 py-0.5 text-xs text-accent">
            @{field.assignee.username}
          </span>
        ) : null}

        {pending.length > 0 ? (
          <span className="flex items-center gap-1 text-xs text-content-subtle">
            <Paperclip className="h-3 w-3" aria-hidden />
            {pending.length}
          </span>
        ) : null}

        <div className="flex-1" />

        <kbd className="hidden text-[11px] text-content-subtle sm:block">
          {pending.length > 0 ? t('common.enterSaves') : t('quickAdd.pasteHint')}
        </kbd>

        <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
          {uploading
            ? t('common.uploading')
            : createTask.isPending
              ? t('common.saving')
              : t('common.add')}
        </Button>
      </div>
    </form>
  );
}

/**
 * The always-available inline "+ Add task" row.
 *
 * On a screen that already has a list, adding should not require a dialog —
 * click, type, Enter, and the field stays open for the next one.
 */
export function InlineAdd({
  onCreate,
  placeholder,
  isPending,
}: {
  onCreate: (input: TaskCreateInput) => void;
  placeholder?: string;
  isPending?: boolean;
}) {
  const t = useT();
  const [active, setActive] = useState(false);

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => setActive(true)}
        className={cn(
          'w-full rounded-md px-2 py-[7px] text-left text-sm',
          'text-content-subtle transition-surface hover:bg-surface-hover hover:text-content',
        )}
      >
        {placeholder ?? t('quickAdd.open')}
      </button>
    );
  }

  // Mounted only while active, so its state starts clean each time.
  return (
    <InlineAddField
      onCreate={onCreate}
      onCancel={() => setActive(false)}
      isPending={isPending}
    />
  );
}

function InlineAddField({
  onCreate,
  onCancel,
  isPending,
}: {
  onCreate: (input: TaskCreateInput) => void;
  onCancel: () => void;
  isPending?: boolean;
}) {
  const t = useT();
  const { field, inputRef } = useMentionInput();
  const submittedRef = useRef(false);

  function submit() {
    const title = field.value.trim();
    if (!title) return;
    onCreate({
      title,
      ...(field.assignee ? { assigned_to_user_id: field.assignee.id } : {}),
    });
    submittedRef.current = true;
    field.reset();
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="relative"
    >
      <input
        ref={inputRef}
        autoFocus
        value={field.value}
        onChange={(event) =>
          field.handleChange(event.target.value, event.target.selectionStart ?? 0)
        }
        onBlur={() => {
          // Collapsing on blur would swallow a half-typed task; only close when
          // nothing was written, and never while the picker holds focus.
          if (!field.value.trim() && !field.mention) onCancel();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !field.mention) {
            event.preventDefault();
            submit();
          } else if (event.key === 'Escape' && !field.mention) {
            field.reset();
            onCancel();
          }
        }}
        disabled={isPending}
        placeholder={t('quickAdd.placeholder')}
        aria-label={t('task.newTitle')}
        className={cn(
          'w-full rounded-md border border-border bg-surface px-2 py-[6px] text-sm',
          'text-content outline-none placeholder:text-content-subtle',
          'focus:border-accent',
        )}
      />
      {field.mention ? (
        <MentionPicker
          query={field.mention.query}
          onSelect={field.selectUser}
          onDismiss={field.dismissMention}
          inputRef={inputRef}
        />
      ) : null}
    </form>
  );
}
